/**
 * The three acts an applicant can perform: accept the terms, send, and send again.
 *
 * EVERY REFUSAL HERE HAS A RULED HANDLING AND NONE OF THEM IS "SHOW AN ERROR".
 *
 *   submit / resubmit, 409 APPLICATION_ALREADY_OPEN — somebody (another tab, a double click) has
 *   already sent it. That is not a failure from the applicant's point of view: RE-READ and show
 *   the in-review screen.
 *
 *   submit / resubmit, 422 TERMS_ACCEPTANCE_STALE — the version moved under them. Send them to the
 *   terms step and bring them straight back; do not make them find it.
 *
 *   submit / resubmit, 422 APPLICATION_INCOMPLETE — a required row is unsatisfied. Re-read the
 *   application so the checklist shows WHICH, rather than repeating a sentence they cannot act on.
 *
 *   resubmit, 409 with NO machine code — "the last round has not been decided needs_more_info, so
 *   there is nothing to resubmit". This 409 carries only the generic Problem, so the branch is on
 *   the status and not on a code. Re-read: the state has moved and the screen is out of date.
 *
 *   acceptTerms, 409 — already accepted. "Double acceptance is impossible by construction." Treat
 *   it as SUCCESS and re-read; showing a failure for a thing that is already true is a lie.
 *
 *   acceptTerms, 404 — no such version, or superseded. Re-read `getCurrentTerms`.
 *
 *   503 anywhere — hold the session and retry. NEVER sign the person out; it means Google could
 *   not be reached, not that they are not who they said they were.
 *
 *   submit / resubmit, 422 UNPROCESSABLE_UNSPECIFIED — G30 (design 22 §1.4): the account has no
 *   way to sign back in, the case both operations' 422 names in authors-api.yaml. It is the one
 *   uncoded 422 either declares, so the generic code IS the branch until the owner mints a word for
 *   it. Not an error to show: the page gives way to the link step (./provider.tsx), and the
 *   person sends again once a sign-in method is on the account.
 *
 * ★ THE LAST STEP'S BOXES GO FIRST (design 25). A submit or a resubmit may carry the submission
 * documents the author ticked on "Our standards and agreement": they are recorded in ONE
 * `acceptAgreements` call - or, where the agreements list could not be read, the author agreement
 * through `acceptTerms`, whose 409 is the same success as above - and only then is the application
 * sent. A 404 on that call is a version superseded while the step was open: re-read, and the step
 * shows the new one.
 */
import { useCallback, useState } from 'react';
import type { Application, TermsState } from '../contract';
import { ApiError, isErrorCode, isStatus } from '../api/problem';
import type { ApplicationApi } from '../api/types';
import type { SubmissionDocument } from '../domain/agreements';
import { useApplicationApi, useRequireSignInMethod } from './provider';

/** What the screen should do next, decided here so the screen does not re-derive it. */
export type ActionOutcome =
  | { readonly kind: 'done' }
  /** The state moved under us. Re-read and render whatever is now true. */
  | { readonly kind: 'stale' }
  /** Send them to the terms step; the reason is already worded. */
  | { readonly kind: 'needs_terms' }
  /** A required checklist row is missing; the list is the explanation. */
  | { readonly kind: 'incomplete' }
  /** G30: the account has no way to sign back in. The link step is already on screen. */
  | { readonly kind: 'needs_sign_in_method' }
  | { readonly kind: 'failed'; readonly error: unknown };

export interface ApplicationActions {
  readonly busy: boolean;
  readonly lastError: unknown;
  readonly acceptTerms: (versionLabel: string) => Promise<ActionOutcome>;
  /** `accept`: the documents ticked on the last step, recorded before the application is sent. */
  readonly submit: (
    versionLabel: string,
    accept?: readonly SubmissionDocument[],
  ) => Promise<ActionOutcome>;
  readonly resubmit: (
    versionLabel: string,
    accept?: readonly SubmissionDocument[],
  ) => Promise<ActionOutcome>;
  readonly clearError: () => void;
}

export interface UseApplicationActionsOptions {
  readonly onApplication: (application: Application) => void;
  readonly onTerms: (terms: TermsState) => void;
  readonly onReload: () => void;
}

export function useApplicationActions(options: UseApplicationActionsOptions): ApplicationActions {
  const api = useApplicationApi();
  const requireSignInMethod = useRequireSignInMethod();
  const { onApplication, onTerms, onReload } = options;
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState<unknown>(null);

  const acceptTerms = useCallback(
    async (versionLabel: string): Promise<ActionOutcome> => {
      setBusy(true);
      setLastError(null);
      try {
        const terms = await api.acceptTerms({ versionLabel });
        onTerms(terms);
        // The acceptance can flip `canSubmit` and the whole application state, so re-read.
        onReload();
        return { kind: 'done' };
      } catch (cause) {
        if (isStatus(cause, 409)) {
          // Already accepted. This is the outcome they wanted.
          onReload();
          return { kind: 'done' };
        }
        if (isStatus(cause, 404)) {
          // The version moved or was superseded between the read and the click.
          onReload();
          return { kind: 'stale' };
        }
        setLastError(cause);
        return { kind: 'failed', error: cause };
      } finally {
        setBusy(false);
      }
    },
    [api, onTerms, onReload],
  );

  const send = useCallback(
    async (
      call: (versionLabel: string) => Promise<Application>,
      versionLabel: string,
      isResubmit: boolean,
      accept: readonly SubmissionDocument[],
    ): Promise<ActionOutcome> => {
      setBusy(true);
      setLastError(null);
      try {
        if (accept.length > 0 && (await recordAcceptances(api, accept)) === 'superseded') {
          onReload();
          return { kind: 'stale' };
        }
        const application = await call(versionLabel);
        onApplication(application);
        // The session's `applicationState` moves too, and the shell renders itself against it.
        onReload();
        return { kind: 'done' };
      } catch (cause) {
        if (isSignInMethodRefusal(cause)) {
          requireSignInMethod();
          return { kind: 'needs_sign_in_method' };
        }
        if (isErrorCode(cause, 'TERMS_ACCEPTANCE_STALE')) {
          onReload();
          return { kind: 'needs_terms' };
        }
        if (isErrorCode(cause, 'APPLICATION_INCOMPLETE')) {
          onReload();
          return { kind: 'incomplete' };
        }
        if (isErrorCode(cause, 'APPLICATION_ALREADY_OPEN')) {
          onReload();
          return { kind: 'stale' };
        }
        // Resubmit's own 409 carries NO registered code — only the generic Problem — so the
        // branch is on the status. It means the round is not in a state that can be resubmitted,
        // which is a stale screen and not a failure to report.
        if (isResubmit && cause instanceof ApiError && cause.status === 409) {
          onReload();
          return { kind: 'stale' };
        }
        setLastError(cause);
        return { kind: 'failed', error: cause };
      } finally {
        setBusy(false);
      }
    },
    [api, onApplication, onReload, requireSignInMethod],
  );

  const submit = useCallback(
    (versionLabel: string, accept: readonly SubmissionDocument[] = []) =>
      send(
        (label) => api.submitApplication({ acceptedTermsVersion: label }),
        versionLabel,
        false,
        accept,
      ),
    [api, send],
  );

  const resubmit = useCallback(
    (versionLabel: string, accept: readonly SubmissionDocument[] = []) =>
      send(
        (label) => api.resubmitApplication({ acceptedTermsVersion: label }),
        versionLabel,
        true,
        accept,
      ),
    [api, send],
  );

  const clearError = useCallback(() => {
    setLastError(null);
  }, []);

  return { busy, lastError, acceptTerms, submit, resubmit, clearError };
}

/**
 * G30's refusal on `submitApplication` / `resubmitApplication`: 422 with the generic code. Both
 * halves are checked - the code alone is the generic word for ANY uncoded 422 on the surface, and
 * the status alone is shared with `APPLICATION_INCOMPLETE` and `TERMS_ACCEPTANCE_STALE`.
 */
function isSignInMethodRefusal(cause: unknown): boolean {
  return isStatus(cause, 422) && isErrorCode(cause, 'UNPROCESSABLE_UNSPECIFIED');
}

/**
 * The last step's documents, recorded in one call (see the header). `superseded`: a version moved
 * while the step was open. Any other refusal is thrown, and reported as the submit's own failure.
 */
async function recordAcceptances(
  api: ApplicationApi,
  accept: readonly SubmissionDocument[],
): Promise<'recorded' | 'superseded'> {
  try {
    const fallback = accept.find((document) => document.via === 'terms');
    if (fallback !== undefined) {
      await api.acceptTerms({ versionLabel: fallback.versionLabel });
    } else {
      await api.acceptAgreements({
        accept: accept.map(({ documentCode, versionLabel }) => ({ documentCode, versionLabel })),
      });
    }
    return 'recorded';
  } catch (cause) {
    // `acceptTerms`' 409: already accepted, which is what was asked for.
    if (isStatus(cause, 409)) return 'recorded';
    if (isStatus(cause, 404)) return 'superseded';
    throw cause;
  }
}
